// What the funnel sits on: the app it leads to, out of focus. The shape is the
// real one — the sidebar and header of the authed layout, the tiles and charts
// of /dashboard — so a lead can tell there is a product waiting behind the
// questions. None of it is data; the numbers only have to look like numbers.
//
// The blurred layer overhangs the viewport because a CSS blur fades to
// transparent at its own edges, which would ring the screen in a pale halo —
// hence the padding that brings the content back on screen.

const NAV = [
  "Dashboard",
  "Cash position",
  "P&L",
  "Revenue",
  "Expense natures",
  "Books",
  "Loans",
  "Dividends",
  "MRR",
  "Income curves",
  "Churn",
  "CAC",
  "Transactions",
  "Stakeholders",
  "Budgets",
  "Rules",
];

const TILES = [
  { label: "Rule of 40", value: "47", delta: "+6 pts" },
  { label: "TTM revenue", value: "$4.9M", delta: "+38%" },
  { label: "Revenue growth", value: "38%", delta: "+4 pts" },
  { label: "Profit margin", value: "9%", delta: "+2 pts" },
  { label: "Available cash", value: "$1.24M", delta: "+$62K" },
  { label: "Monthly net", value: "+$62K", delta: "+11%" },
];

const TRANSACTIONS = [
  ["Sep 30", "Stripe payout", "Revenue", "+$38,412.00"],
  ["Sep 29", "Gusto payroll", "Payroll", "-$61,280.55"],
  ["Sep 29", "Amazon Web Services", "Hosting", "-$8,904.12"],
  ["Sep 28", "Stripe payout", "Revenue", "+$12,077.40"],
  ["Sep 27", "Google Ads", "Marketing", "-$4,350.00"],
  ["Sep 26", "WeWork", "Rent", "-$6,200.00"],
  ["Sep 26", "Stripe payout", "Revenue", "+$9,841.95"],
  ["Sep 25", "Deel", "Contractors", "-$14,600.00"],
  ["Sep 24", "HubSpot", "Software", "-$1,920.00"],
  ["Sep 23", "Stripe payout", "Revenue", "+$21,530.10"],
];

const REVENUE = [310, 322, 318, 341, 356, 349, 372, 388, 401, 396, 418, 431];
const RULE_OF_40 = [31, 34, 33, 38, 41, 39, 44, 47];
const CASH = [820, 860, 845, 910, 980, 950, 1010, 1080, 1060, 1150, 1190, 1240];
const GROWTH = [22, 25, 24, 28, 31, 30, 33, 35, 34, 36, 37, 38];

function Bars({ values, color }: { values: number[]; color: string }) {
  const max = Math.max(...values);
  const slot = 100 / values.length;
  return (
    <svg
      viewBox="0 0 100 50"
      preserveAspectRatio="none"
      className="h-40 w-full"
    >
      {values.map((v, i) => {
        const h = (v / max) * 46;
        return (
          <rect
            key={i}
            x={i * slot + slot * 0.2}
            y={50 - h}
            width={slot * 0.6}
            height={h}
            rx={0.6}
            fill={color}
          />
        );
      })}
    </svg>
  );
}

function Area({ values, color }: { values: number[]; color: string }) {
  const max = Math.max(...values);
  const min = Math.min(...values) * 0.9;
  const points = values.map(
    (v, i) =>
      `${(i / (values.length - 1)) * 100},${
        48 - ((v - min) / (max - min)) * 42
      }`
  );
  const line = `M${points.join(" L")}`;
  return (
    <svg
      viewBox="0 0 100 50"
      preserveAspectRatio="none"
      className="h-40 w-full"
    >
      <path d={`${line} L100,50 L0,50 Z`} fill={color} fillOpacity={0.15} />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth={2}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function Panel({
  title,
  wide,
  children,
}: {
  title: string;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-lg border border-gray-200 bg-white p-4 ${
        wide ? "lg:col-span-2" : ""
      }`}
    >
      <p className="mb-3 text-body font-semibold text-gray-900">{title}</p>
      {children}
    </div>
  );
}

export function DashboardBackdrop() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 -z-10 select-none overflow-hidden bg-gray-50"
    >
      <div className="absolute -inset-6 flex blur-[6px]">
        <aside className="hidden w-[248px] shrink-0 flex-col border-r border-gray-200 bg-white pl-6 pt-6 md:flex">
          <div className="flex h-14 items-center border-b border-gray-200 px-4 text-body font-bold text-gray-900">
            CXCash
          </div>
          <nav className="flex flex-col gap-0.5 p-2">
            {NAV.map((item, i) => (
              <span
                key={item}
                className={`rounded px-3 py-2 text-body font-medium ${
                  i === 0 ? "bg-gray-100 text-gray-900" : "text-gray-600"
                }`}
              >
                {item}
              </span>
            ))}
          </nav>
        </aside>

        <div className="min-w-0 flex-1 pr-6 pt-6">
          <header className="flex h-14 items-center justify-between border-b border-gray-200 bg-white px-8">
            <span className="text-lede font-semibold text-gray-900">
              Dashboard
            </span>
            <div className="hidden gap-2 sm:flex">
              <span className="rounded border border-gray-300 px-3 py-1.5 text-body text-gray-600">
                Last 12 months
              </span>
              <span className="rounded bg-blue-600 px-3 py-1.5 text-body font-medium text-white">
                Generate board pack
              </span>
            </div>
          </header>

          <div className="grid gap-4 p-8">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
              {TILES.map((tile) => (
                <div
                  key={tile.label}
                  className="rounded-lg border border-gray-200 bg-white p-4"
                >
                  <p className="text-caption text-gray-500">{tile.label}</p>
                  <p className="mt-1 text-display-sm font-semibold text-gray-900">
                    {tile.value}
                  </p>
                  <p className="mt-1 text-caption text-emerald-600">
                    {tile.delta}
                  </p>
                </div>
              ))}
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
              <Panel title="Monthly revenue" wide>
                <Bars values={REVENUE} color="#3b82f6" />
              </Panel>
              <Panel title="Rule of 40">
                <Bars values={RULE_OF_40} color="#10b981" />
              </Panel>
              <Panel title="Cash position" wide>
                <Area values={CASH} color="#2563eb" />
              </Panel>
              <Panel title="TTM revenue growth">
                <Area values={GROWTH} color="#f59e0b" />
              </Panel>
            </div>

            <Panel title="Recent transactions">
              <table className="w-full text-body">
                <tbody>
                  {TRANSACTIONS.map(([date, who, category, amount], i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="py-2 text-gray-500">{date}</td>
                      <td className="py-2 font-medium text-gray-900">{who}</td>
                      <td className="py-2 text-gray-500">{category}</td>
                      <td
                        className={`py-2 text-right ${
                          amount.startsWith("+")
                            ? "text-emerald-600"
                            : "text-gray-900"
                        }`}
                      >
                        {amount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          </div>
        </div>
      </div>
      <div className="absolute inset-0 bg-zinc-900/10" />
    </div>
  );
}
